import moment from 'moment/moment';
import classNames from 'classnames';
import safelyAccessNestedProperties from './safelyAccessNestedProperties';
import { findAddressByFulfillment } from './fulfillment';
import { STATUS } from '../constants/status';
import { FULFILLMENTS } from '../constants';
import {
  formatBodfsDeliveryWindow,
  formatApplianceDeliveryWindow,
  getShippingDeliveryDate,
  isDateGMT,
} from './format';
import { validateTYTDisplay } from './trackYourTruck';

declare var THD: any;

export const getShipmentPackageByIndex = (
  shipments: Object,
  shipmentIndex: Number
) => {
  if (!shipmentIndex) {
    return null;
  }
  const shipmentPackage = [];
  shipmentPackage.push(shipments[shipmentIndex]);
  return shipmentPackage;
};

export const getTrackingFlag = (actions: Object) =>
  safelyAccessNestedProperties(['trackingEnabled'], actions)
    ? actions.trackingEnabled
    : false;

export const setLineItemKey = (
  group: Object,
  shipmentIndex: Number,
  lineId: Number
) => {
  const isBODFSorDFS =
    group.fulfillment === FULFILLMENTS.FULFILLMENT_METHOD_BODFS ||
    group.fulfillment === FULFILLMENTS.FULFILLMENT_METHOD_DFS;
  const isReturnedOrCanceled =
    group.statusCode === 150 ||
    group.statusCode === 151 ||
    group.statusCode === 140;
  if (!shipmentIndex) {
    return lineId;
  }
  if (!isBODFSorDFS && !isReturnedOrCanceled) {
    return group.shipments[shipmentIndex].trackingNumber;
  }
  return shipmentIndex;
};
export const getZipCode = (fulfillmentType: string, fulfillment: object) => {
  const address = findAddressByFulfillment(fulfillmentType, fulfillment);
  return address ? address.zipCode : null;
};

export const set121Days = (
  fulfillmentMethod: string,
  statusCode: number,
  trackingEnabled: boolean
) =>
  fulfillmentMethod === FULFILLMENTS.FULFILLMENT_METHOD_STH &&
  statusCode === STATUS.DELIVERED &&
  !trackingEnabled;

export const setTrackingButtonVisibility = (
  statusCode: Number,
  actions: Boolean,
  fulfillmentMethod: String,
  statusUpdatedDate: String,
  depotDirect: Object,
  bodfsDeliveries: Object,
  shipments: Object,
  shippingAndHandling: object,
  shipmentIndex: Number
) => {
  const isBackordered = statusCode === STATUS.BACKORDERED;
  const isCanceled = statusCode === STATUS.CANCELED;
  const isReturned = statusCode === STATUS.RETURNED;
  const isDelivered = statusCode === STATUS.DELIVERED;
  const isReturnInProgress = statusCode === STATUS.RETURN_IN_PROGRESS;
  const isTrackingEnabled = actions ? !!actions.trackingEnabled : false;
  const is121Days = set121Days(
    fulfillmentMethod,
    statusCode,
    isTrackingEnabled
  );
  // trackingEnabled will not be defined if order group has no shipments
  // this issue should be fixed on the backend
  const isTrackingFallback =
    isTrackingEnabled || (actions && actions.trackingEnabled === undefined);
  if (
    fulfillmentMethod === FULFILLMENTS.FULFILLMENT_METHOD_STH ||
    fulfillmentMethod === FULFILLMENTS.FULFILLMENT_METHOD_BOSS
  ) {
    if (is121Days) {
      return false;
    }
    if (
      !isBackordered &&
      !isCanceled &&
      !isReturned &&
      isTrackingFallback &&
      safelyAccessNestedProperties(['trackingURL'], shippingAndHandling)
    ) {
      return true;
    }
    if (
      !safelyAccessNestedProperties(
        [shipmentIndex, 'trackingNumber'],
        shipments
      )
    ) {
      return false;
    }
  }

  if (fulfillmentMethod === FULFILLMENTS.FULFILLMENT_METHOD_APPLIANCE) {
    if (!depotDirect) return false;
    return statusCode !== STATUS.DELIVERED && statusCode !== STATUS.COMPLETED;
  }

  if (
    fulfillmentMethod === FULFILLMENTS.FULFILLMENT_METHOD_BODFS ||
    fulfillmentMethod === FULFILLMENTS.FULFILLMENT_METHOD_DFS
  ) {
    if (!bodfsDeliveries) return false;
    if (
      !isBackordered &&
      !isCanceled &&
      !isReturned &&
      bodfsDeliveries.reservationWindowStartTime
    ) {
      if (isDelivered) {
        return validateTYTDisplay(statusUpdatedDate);
      }
      return true;
    }
    return false;
  }

  const isProcessingReadyForPickupOrOnSchedule =
    statusCode === STATUS.PROCESSING ||
    statusCode === STATUS.READY_FOR_PICKUP ||
    statusCode === STATUS.BODFS_ON_SCHEDULE;
  return (
    isProcessingReadyForPickupOrOnSchedule ||
    (!isBackordered &&
      !isCanceled &&
      !isReturned &&
      isTrackingEnabled &&
      !isReturnInProgress)
  );
};

export const formatFulfillmentMethod = (fulfillmentMethod: Object) => {
  switch (fulfillmentMethod) {
    case FULFILLMENTS.FULFILLMENT_METHOD_BOSS:
      return FULFILLMENTS.BOSS;
    case FULFILLMENTS.FULFILLMENT_METHOD_STH:
      return FULFILLMENTS.STH;
    case FULFILLMENTS.FULFILLMENT_METHOD_BODFS:
      return FULFILLMENTS.BODFS;
    case FULFILLMENTS.FULFILLMENT_METHOD_APPLIANCE:
      return FULFILLMENTS.APPLIANCE;
    case FULFILLMENTS.FULFILLMENT_METHOD_BOPIS:
      return FULFILLMENTS.BOPIS;
    case FULFILLMENTS.FULFILLMENT_METHOD_MISC:
      return FULFILLMENTS.MISC;
    case FULFILLMENTS.FULFILLMENT_METHOD_DFS:
      return FULFILLMENTS.DFS;
    default: {
      console.error(
        `FulfillmentMethod: ${fulfillmentMethod} Unknown. Defaulting to STH`
      );
      return FULFILLMENTS.STH;
    }
  }
};

export const setFirstNameByGroup = (group: Object) => {
  const { fulfillment } = group;
  switch (fulfillment) {
    case FULFILLMENTS.FULFILLMENT_METHOD_STH:
      return group.name ? group.name.firstName : '';
    case FULFILLMENTS.FULFILLMENT_METHOD_BOSS:
      return group.storeName;
    case FULFILLMENTS.FULFILLMENT_METHOD_BODFS:
    case FULFILLMENTS.FULFILLMENT_METHOD_DFS:
      return group.name ? group.name.firstName : '';
    case FULFILLMENTS.FULFILLMENT_METHOD_APPLIANCE:
      return group.name ? group.name.firstName : '';
    case FULFILLMENTS.FULFILLMENT_METHOD_BOPIS:
      return group.storeName;
    default: {
      console.error(
        `Group: ${fulfillment} Unknown. Cannot set proper name. Defaulting to STH`
      );
      return group.name ? group.name.firstName : '';
    }
  }
};

export const setLastNameByGroup = (group: Object) => {
  const { fulfillment } = group;
  switch (fulfillment) {
    case FULFILLMENTS.FULFILLMENT_METHOD_STH:
      return group.name ? group.name.lastName : '';
    case FULFILLMENTS.FULFILLMENT_METHOD_BOSS:
      return '';
    case FULFILLMENTS.FULFILLMENT_METHOD_BODFS:
    case FULFILLMENTS.FULFILLMENT_METHOD_DFS:
      return group.name ? group.name.lastName : '';
    case FULFILLMENTS.FULFILLMENT_METHOD_APPLIANCE:
      return group.name ? group.name.lastName : '';
    case FULFILLMENTS.FULFILLMENT_METHOD_BOPIS:
      return '';
    default: {
      console.error(
        `Group: ${fulfillment} Unknown. Cannot set proper name. Defaulting to STH`
      );
      return group.name ? group.name.lastName : '';
    }
  }
};

export const getFulfillmentLocation = (
  fulfillment: String,
  fulfillmentMethod: String
) => {
  switch (fulfillmentMethod) {
    case FULFILLMENTS.FULFILLMENT_METHOD_STH:
    case FULFILLMENTS.FULFILLMENT_METHOD_MISC:
      // return THD.MyAccount.Utility && THD.MyAccount.Utility.getTHDStoreZip();
      return THD.Utils.User && THD.Utils.User.getTHDStoreZip();
    case FULFILLMENTS.FULFILLMENT_METHOD_BOSS:
      return (
        fulfillment &&
        fulfillment.bossShippingAndHandling &&
        fulfillment.bossShippingAndHandling.pickUpDetails &&
        fulfillment.bossShippingAndHandling.pickUpDetails.store &&
        fulfillment.bossShippingAndHandling.pickUpDetails.store.number
      );
    case FULFILLMENTS.FULFILLMENT_METHOD_DFS:
      return (
        fulfillment &&
        fulfillment.bodfsDeliveryAndHandling &&
        fulfillment.bodfsDeliveryAndHandling.bodfsDeliveries &&
        fulfillment.bodfsDeliveryAndHandling.bodfsDeliveries[0] &&
        fulfillment.bodfsDeliveryAndHandling.bodfsDeliveries[0].storeId
      );
    case FULFILLMENTS.FULFILLMENT_METHOD_BOPIS:
    case FULFILLMENTS.FULFILLMENT_METHOD_APPLIANCE:
    case FULFILLMENTS.FULFILLMENT_METHOD_BODFS:
      return null;
    default: {
      console.error(
        `Group: ${fulfillmentMethod} Unknown. Cannot set proper fulfillmentLocation. Defaulting to STH`
      );
      // return THD.MyAccount.Utility && THD.MyAccount.Utility.getTHDStoreZip();
      return THD.Utils.User && THD.Utils.User.getTHDStoreZip();
    }
  }
};

export const isMultiplePackagesFromGroup = (group: Object) => {
  const fulfillmentMethod = group.fulfillment;
  if (
    group.statusCode === STATUS.CANCELED ||
    group.statusCode === STATUS.BACKORDERED ||
    group.statusCode === STATUS.RETURNED
  ) {
    return false;
  }
  switch (fulfillmentMethod) {
    case FULFILLMENTS.FULFILLMENT_METHOD_STH: {
      if (group.shipments) {
        const isLineHaul =
          safelyAccessNestedProperties(['isLineHaul'], group.actions) === true;
        const isMultiplePackages = isLineHaul
          ? false
          : group.shipments.length > 1;
        return isMultiplePackages;
      }
      return false;
    }
    case FULFILLMENTS.FULFILLMENT_METHOD_BOSS: {
      if (group.shipments) return group.shipments.length > 1;
      return false;
    }
    case FULFILLMENTS.FULFILLMENT_METHOD_DFS:
    case FULFILLMENTS.FULFILLMENT_METHOD_BODFS: {
      if (group.bodfsWorkOrderNumbers)
        return group.bodfsWorkOrderNumbers.length > 1;
      return false;
    }
    default: {
      return false;
    }
  }
};

export const setShipmentsByFulfillment = (
  fulfillmentMethod: String,
  shipmentIndex: Object,
  isLineHaul: Boolean,
  shipments: Object
) => {
  if (!shipments) {
    return [];
  }
  if (isLineHaul) {
    return shipments;
  }
  if (
    fulfillmentMethod === FULFILLMENTS.FULFILLMENT_METHOD_STH ||
    fulfillmentMethod === FULFILLMENTS.FULFILLMENT_METHOD_BOSS
  ) {
    const shipmentPackage = [];
    shipmentPackage.push(shipments[shipmentIndex]);
    return shipmentPackage;
  }
  if (
    fulfillmentMethod === FULFILLMENTS.FULFILLMENT_METHOD_BODFS ||
    fulfillmentMethod === FULFILLMENTS.FULFILLMENT_METHOD_DFS
  ) {
    return [];
  }
  return [];
};

export const getDeliveryDateByGroup = (
  group: Object,
  statusUpdatedDate: String,
  shipmentIndex: Number,
  bodfsDeliveries: Object,
  depotDirect: Object
) => {
  if (group.fulfillment === FULFILLMENTS.FULFILLMENT_METHOD_APPLIANCE) {
    if (depotDirect) {
      return depotDirect.currentDeliveryDate;
    }
    return statusUpdatedDate;
  }

  if (
    group.statusCode === STATUS.RETURNED ||
    group.statusCode === STATUS.DELIVERED ||
    group.statusCode === STATUS.COMPLETED
  ) {
    if (
      isDateGMT(statusUpdatedDate) &&
      !group.fulfillment === FULFILLMENTS.FULFILLMENT_METHOD_BODFS
    ) {
      const formattedGMTDate = moment(statusUpdatedDate).format('YYYY-MM-DD');
      return formattedGMTDate;
    }
    return statusUpdatedDate;
  }
  if (
    group.fulfillment === FULFILLMENTS.FULFILLMENT_METHOD_BODFS ||
    group.fulfillment === FULFILLMENTS.FULFILLMENT_METHOD_DFS
  ) {
    const isReservationWindowStartAvailable = safelyAccessNestedProperties(
      ['reservationWindowStartTime'],
      bodfsDeliveries
    );
    if (!bodfsDeliveries || !isReservationWindowStartAvailable) {
      return '';
    }
    return bodfsDeliveries.reservationWindowStartTime;
  }

  if (group.fulfillment === FULFILLMENTS.FULFILLMENT_METHOD_BOPIS) {
    const { statusCode, comEstEndDeliveryDate, pickupDate } = group;
    switch (statusCode) {
      case STATUS.PROCESSING:
        return comEstEndDeliveryDate || pickupDate;
      case STATUS.READY_FOR_PICKUP:
        return statusUpdatedDate || comEstEndDeliveryDate || pickupDate;
      case STATUS.COMPLETED:
        return statusUpdatedDate || comEstEndDeliveryDate || pickupDate;
      default: {
        console.error(
          `Status: ${statusCode} Unknown. Cannot set proper fulfillmentLocation. Defaulting date.`
        );
        return comEstEndDeliveryDate || pickupDate || statusUpdatedDate;
      }
    }
  }

  if (group.fulfillment === FULFILLMENTS.FULFILLMENT_METHOD_BOSS) {
    const { statusCode, comEstEndDeliveryDate, estDeliveryDate } = group;

    switch (statusCode) {
      case STATUS.PROCESSING:
        return comEstEndDeliveryDate;
      case STATUS.ON_ITS_WAY_TO_STORE:
        return estDeliveryDate || comEstEndDeliveryDate;
      case STATUS.PREPARING_FOR_PICKUP:
        return comEstEndDeliveryDate;
      case STATUS.READY_FOR_PICKUP:
        return statusUpdatedDate;
      case STATUS.COMPLETED:
        return statusUpdatedDate;
      default: {
        console.error(
          `Status: ${statusCode} Unknown. Cannot set proper fulfillmentLocation. Defaulting date.`
        );
        return comEstEndDeliveryDate;
      }
    }
  }
  return getShippingDeliveryDate(group, shipmentIndex);
};

export const setGroupDivider = (
  multiPackageHeaderShipments: Object,
  shipmentIndex: number,
  isLineHaul: boolean
) => {
  if (!multiPackageHeaderShipments || isLineHaul) {
    return classNames({
      'u__order--group': true,
      'u__border--bottom': true,
    });
  }
  return classNames({
    'u__order--group': true,
    'u__border--bottom':
      shipmentIndex === multiPackageHeaderShipments.length - 1,
  });
};

export const setItemDivider = (
  multiPackageHeaderShipments: Object,
  shipmentIndex: number,
  isLineHaul: Boolean
) => {
  if (!multiPackageHeaderShipments) {
    return classNames({
      grid: true,
      'u--marginLarge-top': true,
      'u--margin-bottom': true,
    });
  }
  const isVisible =
    !isLineHaul && shipmentIndex !== multiPackageHeaderShipments.length - 1;
  return classNames({
    grid: true,
    'u--marginLarge-top': true,
    'u--margin-bottom': true,
    'u__border--bottom': isVisible,
  });
};
// TODO: Merge with dev and remove
export const getQuantity = (lineItem: Object, statusCode: number) => {
  const { cancelledQuantity, returnedQuantity, currentQuantity } = lineItem;
  const isCanceled = statusCode === STATUS.CANCELED;
  const isReturned = statusCode === STATUS.RETURNED;
  const quantity =
    (isCanceled && cancelledQuantity) ||
    (isReturned && returnedQuantity) ||
    currentQuantity;
  return quantity;
};

export const getTimeWindowPayload = (
  depotDirect: Object,
  bodfsDeliveries: Object
) => {
  if (bodfsDeliveries) {
    const {
      reservationWindowStartTime,
      reservationWindowEndTime,
    } = bodfsDeliveries;
    const timeWindowPayload = {
      deliveryBeginTime: formatBodfsDeliveryWindow(reservationWindowStartTime),
      deliveryEndTime: formatBodfsDeliveryWindow(reservationWindowEndTime),
    };
    return timeWindowPayload;
  }
  if (depotDirect) {
    const timeWindowPayload = {
      deliveryBeginTime: formatApplianceDeliveryWindow(
        depotDirect.deliveryBeginTime
      ),
      deliveryEndTime: formatApplianceDeliveryWindow(
        depotDirect.deliveryEndTime
      ),
    };
    return timeWindowPayload;
  }
  return { deliveryBeginTime: '', deliveryEndTime: '' };
};

/**
 * function that returns item quantity based on status
 * @param {object} lineItem
 * @param {number} statusCode
 */
export const getQuantityByStatus = (lineItem: Object, statusCode: number) => {
  const { currentQuantity, cancelledQuantity, returnedQuantity } = lineItem;
  if (statusCode === STATUS.CANCELED) {
    return cancelledQuantity || '';
  }
  if (statusCode === STATUS.RETURNED) {
    return returnedQuantity || '';
  }
  return currentQuantity || '';
};

/**
 * function that returns item unit price based on status
 * @param {Object} lineItem
 * @param {number} statusCode
 */
export const getUnitPriceByStatus = (lineItem: Object, statusCode: number) => {
  const { unitPrice, returnedUnitPrice } = lineItem;
  if (statusCode === STATUS.CANCELED) {
    return '';
  }
  if (statusCode === STATUS.RETURNED) {
    return returnedUnitPrice || '';
  }
  return unitPrice;
};

/**
 * function that returns item total price based on status
 * @param {Object} lineItem
 * @param {number} statusCode
 */
export const getTotalPriceByStatus = (lineItem: Object, statusCode: number) => {
  const { totalPrice, returnedTotalPrice } = lineItem;
  return statusCode === STATUS.RETURNED ? returnedTotalPrice : totalPrice;
};

export const makeHeaders = () => {
  const headers = {};
  headers.c2 =
    document.getElementById('hat_env_id') !== null
      ? document.getElementById('hat_env_id').value
      : '';
  headers.ts1 =
    document.getElementById('hat_env_ts') !== null
      ? document.getElementById('hat_env_ts').value
      : '';
  headers.t3 =
    document.getElementById('hat_env') !== null
      ? document.getElementById('hat_env').value
      : '';
  headers.d4 = headers.ts1 !== '' ? new Date().getTime() - headers.ts1 : 20000;
  return headers;
};
